import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { savePersistedLogin, loadPersistedLogin, clearPersistedLogin, isTeacherOrStudentRole } from '../../lib/persistedAuth';
import { useRealtimeAccessGuard } from '../../lib/useRealtimeAccessGuard';
import AccessGuardOverlay from '../../components/AccessGuardOverlay/AccessGuardOverlay';
import Logo from '../../assets/Logo.svg';
import './Profile.css';
import '../PasswordVerification/PasswordVerification.css';

const capitalize = (s) => (s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : '');

// Helpers to initialize from session for smoother first render
const getRawSessionUser = () => {
  try {
    const userDataStr = sessionStorage.getItem('currentUser');
    if (!userDataStr) return null;
    return JSON.parse(userDataStr);
  } catch (e) {

    return null;
  }
};

const buildCurrentUserFromRaw = (userData) => {
  if (!userData) return null;
  return {
    id: userData.id,
    // email: userData.email,
    user_metadata: {
      full_name: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
      first_name: userData.first_name,
      last_name: userData.last_name,
    },
    app_metadata: {
      role: userData.role,
      school_id: userData.school_id,
      school: userData.schools,
    }
  };
};

const Profile = () => {
  const rawSessionUser = getRawSessionUser();
  const initialCurrentUser = buildCurrentUserFromRaw(rawSessionUser);

  const [currentUser, setCurrentUser] = useState(initialCurrentUser);
  const [loading, setLoading] = useState(!initialCurrentUser);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [schoolName, setSchoolName] = useState(null);
  const [userStats, setUserStats] = useState(() => ({
    gradeLabel: rawSessionUser?.grade || '—',
    lastLogin: rawSessionUser ? new Date().toLocaleDateString() : null,
    favoriteProviders: rawSessionUser ? ['Bookr', 'Sam Learning', 'Boclips'] : [],
    assignmentsCount: 0
  }));

  const navigate = useNavigate();
  const location = useLocation();

  // Realtime access guard: kick user out if their account is deactivated or school subscription becomes inactive.
  const { blockInfo, acknowledge } = useRealtimeAccessGuard({
    userId: currentUser?.id || rawSessionUser?.id || null,
    schoolId: currentUser?.app_metadata?.school_id || rawSessionUser?.school_id || null,
  });

  // Navigation items
  const navItems = [
    { label: 'Home', path: '/home' },
    { label: 'Calendar', path: '/calendar' },
    { label: 'Profile', path: '/profile' },
  ];

  // Form state
  const [formData, setFormData] = useState(() => ({
    first_name: rawSessionUser?.first_name || '',
    last_name: rawSessionUser?.last_name || '',
    // email: rawSessionUser?.email || '',
    phone: rawSessionUser?.phone || '',
    password: rawSessionUser?.password || ''
  }));

  const fetchCurrentUser = async () => {

    try {
      // Get user data from sessionStorage (set by Hero component)
      const userDataStr = sessionStorage.getItem('currentUser');

      if (!userDataStr) {

        setError('Please enter your User ID on the home page to access your profile');
        return;
      }

      const userData = JSON.parse(userDataStr);

      // Create a user object that matches the expected format
      const user = {
        id: userData.id,
        // email: userData.email,
        user_metadata: {
          full_name: userData.full_name || `${userData.first_name} ${userData.last_name}`,
          first_name: userData.first_name,
          last_name: userData.last_name,
        },
        app_metadata: {
          role: userData.role,
          school_id: userData.school_id,
          school: userData.schools,
        }
      };

      setCurrentUser(user);

      // Extract school name from user data
      if (userData.schools?.name) {
        setSchoolName(userData.schools.name);
      } else if (userData.school_id) {
        // Fetch school name if not in session
        supabase
          .from('schools')
          .select('name')
          .eq('id', userData.school_id)
          .single()
          .then(({ data: schoolData }) => {
            if (schoolData?.name) {
              setSchoolName(schoolData.name);
            }
          })
          .catch(err => {

          });
      }

      // Set form data immediately with existing data
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        // email: userData.email || '',
        phone: userData.phone || '',
        password: userData.password || '' // Use existing password from session
      });

      // Initialize user stats from session
      setUserStats(prev => ({
        ...prev,
        gradeLabel: userData.grade || '—',
        lastLogin: new Date().toLocaleDateString(),
      }));


      // Fetch password and phone from database in background (non-blocking)
      supabase
        .from('users')
        .select('password, phone')
        .eq('id', userData.id)
        .single()
        .then(({ data: userPasswordData, error: passwordError }) => {
          if (!passwordError && userPasswordData) {
            setFormData(prev => ({
              ...prev,
              password: userPasswordData.password || prev.password,
              phone: userPasswordData.phone || prev.phone
            }));
          }
        })
        .catch(err => {

          // Don't show error to user, just log it
        });

    } catch (err) {

      setError('Invalid user session. Please enter your User ID again.');
    } finally {
      setLoading(false);

    }
  };

  // Fetch assignment count for this logged-in user
  useEffect(() => {
    let cancelled = false;

    async function loadAssignmentStats() {
      try {
        const raw = getRawSessionUser();
        if (!raw?.id) return;

        // Ensure grade label reflects any session updates
        const gradeLabel = raw.grade || '—';
        if (!cancelled) {
          setUserStats(prev => ({ ...prev, gradeLabel }));
        }

        // Teacher: assignments created by this teacher
        if (raw.role === 'teacher') {
          const { count, error: countErr } = await supabase
            .from('calendar_assignments')
            .select('id', { count: 'exact', head: true })
            .eq('created_by', raw.id);

          if (countErr) throw countErr;
          if (!cancelled) setUserStats(prev => ({ ...prev, assignmentsCount: count || 0 }));
          return;
        }

        // Student: assignments for (school + grade)
        if (raw.role === 'student') {
          let gradeId = raw.grade_id || null;

          // If grade_id isn't present yet, attempt to map grade name -> grade_id via grades table
          if (!gradeId && raw.school_id && raw.grade) {
            const { data: gradeRow } = await supabase
              .from('grades')
              .select('id, name')
              .eq('school_id', raw.school_id)
              .eq('name', raw.grade)
              .maybeSingle();
            gradeId = gradeRow?.id || null;
          }

          if (!raw.school_id || !gradeId) {
            if (!cancelled) setUserStats(prev => ({ ...prev, assignmentsCount: 0 }));
            return;
          }

          const { count, error: countErr } = await supabase
            .from('calendar_assignments')
            .select('id', { count: 'exact', head: true })
            .eq('school_id', raw.school_id)
            .eq('grade_id', gradeId);

          if (countErr) throw countErr;
          if (!cancelled) setUserStats(prev => ({ ...prev, assignmentsCount: count || 0 }));
        }
      } catch (e) {
        // If table/bucket isn't set up yet, fail gracefully
        if (!cancelled) setUserStats(prev => ({ ...prev, assignmentsCount: 0 }));
      }
    }

    loadAssignmentStats();
    return () => { cancelled = true; };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'password') {
      // Only allow numbers for password field
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePasswordSquareClick = () => {
    const hiddenInput = document.getElementById('profile-password-input');
    if (hiddenInput) {
      hiddenInput.focus();
    }
  };

  const handleEdit = () => {
    try {
      setEditing(true);
      setError(null);
      setSuccess(null);

      // Scroll to the profile form section after a short delay to ensure it's rendered
      setTimeout(() => {
        const formSection = document.getElementById('profile-form-section');
        if (formSection) {
          formSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } catch (err) {

      setError('Failed to start editing. Please try again.');
    }
  };

  // inside Profile.jsx — replace your existing handleSave with this
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Get current user data
      const userDataStr = sessionStorage.getItem('currentUser');
      if (!userDataStr) {
        setError('User session not found. Please log in again.');
        return;
      }

      const userData = JSON.parse(userDataStr);

      // Basic client-side validation (password must be 6 digits if provided)
      if (formData.password && !/^\d{6}$/.test(formData.password)) {
        setError('Password must be exactly 6 digits (numbers only).');
        return;
      }

      // Build update payload — DO NOT include generated column `full_name`
      const updatePayload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        // email: formData.email,
        phone: formData.phone
      };

      // Only include password if user changed it (optional)
      if (formData.password) {
        updatePayload.password = formData.password;
      }

      // Perform update
      const { data, error: updateError } = await supabase
        .from('users')
        .update(updatePayload)
        .eq('id', userData.id)
        .select(); // optional: returns the updated row(s)

      if (updateError) {

        setError('Failed to save profile. Please try again.');
        return;
      }

      // Merge updated values into session storage (don't worry about full_name — DB will generate it)
      const updatedUserData = {
        ...userData,
        ...formData,
        // If you want to show a client-side full_name right away, compute it locally:
        full_name: `${formData.first_name} ${formData.last_name}`
      };

      sessionStorage.setItem('currentUser', JSON.stringify(updatedUserData));

      setSuccess('Profile updated successfully!');
      setEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {

      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setError(null);
    setSuccess(null);
    // Reset form data to original values without refetching
    const userDataStr = sessionStorage.getItem('currentUser');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        // email: userData.email || '',
        phone: userData.phone || '',
        password: userData.password || ''
      });
    }
  };

  const handleLogout = () => {
    // Clear persisted login (localStorage) so Hero page doesn't show quick access
    clearPersistedLogin();

    // Clear all session data
    sessionStorage.removeItem('currentUser');
    sessionStorage.removeItem('pendingLogin');

    // Force redirect to landing page and prevent back navigation
    window.location.href = '/';
  };

  const handleNavClick = (path) => navigate(path);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  if (loading) {
    return (
      <div className="profile-page">
        {blockInfo && (
          <AccessGuardOverlay title={blockInfo.title} message={blockInfo.message} onAcknowledge={acknowledge} />
        )}
        <div className="profile-loading">
          <div className="loading-spinner">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
            </svg>
          </div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="profile-page">
        {blockInfo && (
          <AccessGuardOverlay title={blockInfo.title} message={blockInfo.message} onAcknowledge={acknowledge} />
        )}
        <div className="profile-error">
          <div className="error-icon">⚠️</div>
          <h2>Access Required</h2>
          <p>Please enter your User ID on the home page to access your profile.</p>
          <button
            className="profile-btn primary-btn"
            onClick={() => navigate('/')}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {blockInfo && (
        <AccessGuardOverlay title={blockInfo.title} message={blockInfo.message} onAcknowledge={acknowledge} />
      )}
      {/* Header - match dashboard header style */}
      <header className="provider-page-header">
        <div className="provider-page-header-inner">
          <div
            className="header-left"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          >
            <img src={Logo} alt="MyDigitalSchool" className="header-logo" />
            <h1>My Digital Schools</h1>
          </div>
          <div className="header-right">
            <nav className="provider-page-nav">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  className={`provider-page-nav-item ${location.pathname === item.path ? 'active' : ''
                    }`}
                  onClick={() => handleNavClick(item.path)}
                >
                  <span className="nav-label">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="page-content-wrapper">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-header-content">
            <div className="profile-avatar">
              <div className="avatar-circle">
                {currentUser.user_metadata?.first_name?.[0] || currentUser.email?.[0] || 'U'}
              </div>
            </div>
            <div className="profile-info">
              <h1 className="profile-name">
                {currentUser.user_metadata?.full_name || 'User Profile'}
              </h1>
              <p className="profile-email">{currentUser.email}</p>
              <p className="profile-role">{capitalize(currentUser.app_metadata?.role || '')}</p>
              {schoolName && (
                <div className="school-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span>{schoolName}</span>
                </div>
              )}
            </div>
          </div>

          <div className="profile-actions">
            {!editing ? (
              <div className="profile-action-buttons">
                <button
                  className="profile-btn edit-btn"
                  onClick={handleEdit}
                >
                  Edit Profile
                </button>
                <button
                  className="profile-btn profile-logout-btn"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="edit-actions">
                <button
                  className="profile-btn cancel-btn"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="profile-btn save-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="profile-message error-message">
            <span className="message-icon">⚠️</span>
            <span className="message-text">{error}</span>
            <button
              className="message-close"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="profile-message success-message">
            <span className="message-icon">✅</span>
            <span className="message-text">{success}</span>
            <button
              className="message-close"
              onClick={() => setSuccess(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Profile Content */}
        <div className="profile-content">
          {/* User Stats */}
          <div className="profile-stats">
            <h2 className="profile-section-title">Your Activity</h2>
            <div className="profile-stats-grid">
              <div className="profile-stat-card">
                <div className="profile-stat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="profile-stat-content">
                  <div className="profile-stat-number">{userStats.gradeLabel || '—'}</div>
                  <div className="profile-stat-label">
                    {userStats.gradeLabel && userStats.gradeLabel.includes(',') ? 'Grades' : 'Grade'}
                  </div>
                </div>
              </div>
              <div className="profile-stat-card">
                <div className="profile-stat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="profile-stat-content">
                  <div className="profile-stat-number">{userStats.assignmentsCount}</div>
                  <div className="profile-stat-label">
                    {userStats.assignmentsCount === 1 ? 'Assignment' : 'Assignments'}
                  </div>
                </div>
              </div>
              <div className="profile-stat-card">
                <div className="profile-stat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="profile-stat-content">
                  <div className="profile-stat-number">{userStats.favoriteProviders.length}</div>
                  <div className="profile-stat-label">
                    {userStats.favoriteProviders.length === 1 ? 'Accessible Provider' : 'Accessible Providers'}
                  </div>
                </div>
              </div>
              <div className="profile-stat-card">
                <div className="profile-stat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor" />
                  </svg>
                </div>
                <div className="profile-stat-content">
                  <div className="profile-stat-number">{userStats.lastLogin}</div>
                  <div className="profile-stat-label">Last Login</div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          {editing && (
            <div className="profile-form-section" id="profile-form-section">
              <h2 className="profile-section-title">Profile Information</h2>
              <div className="profile-form">
                <div className="profile-form-row">
                  <div className="profile-form-group">
                    <label htmlFor="first_name">First Name</label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      disabled={!editing}
                      className={editing ? 'profile-editable' : 'profile-readonly'}
                    />
                  </div>
                  <div className="profile-form-group">
                    <label htmlFor="last_name">Last Name</label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      disabled={!editing}
                      className={editing ? 'profile-editable' : 'profile-readonly'}
                    />
                  </div>
                </div>

                <div className="profile-form-row">
                  {/* <div className="profile-form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!editing}
                      className={editing ? 'profile-editable' : 'profile-readonly'}
                    />
                  </div> */}
                  <div className="profile-form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!editing}
                      className={editing ? 'profile-editable' : 'profile-readonly'}
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>


                <div className="profile-form-group">
                  <label htmlFor="password">Password</label>
                  {/* Modern 6-square password UI (reusing PasswordVerification styles) */}
                  <div className="password-squares-container">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`password-square ${formData.password.length > index ? 'filled' : ''} ${formData.password.length === index ? 'active' : ''}`}
                        onClick={handlePasswordSquareClick}
                      >
                        {formData.password[index] || ''}
                      </div>
                    ))}
                  </div>
                  {/* Hidden input that actually controls the password value */}
                  <input
                    type="password"
                    id="profile-password-input"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className="password-hidden-input"
                    placeholder="Enter 6-digit password (numbers only)"
                    maxLength="6"
                    autoComplete="new-password"
                  />
                  <small className="profile-field-help">Password must be 6 digits (numbers only)</small>
                </div>

              </div>
            </div>
          )}


          {/* Favorite Providers */}
          {/* <div className="profile-favorite-providers">
            <h2 className="profile-section-title">Your Favorite Providers</h2>
            <div className="profile-providers-list">
              {userStats.favoriteProviders.map((provider, index) => (
                <div key={index} className="profile-provider-item">
                  <div className="profile-provider-icon">
                    {provider.charAt(0)}
                  </div>
                  <div className="profile-provider-name">{provider}</div>
                </div>
              ))}
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Profile;
